import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!mounted) return;
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        setPasswordRecovery(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    setPasswordRecovery(false);
    return { error };
  };

  /** Re-check the signed-in user's password before sensitive account changes. */
  const reauthenticate = async (password) => {
    const email = session?.user?.email;
    if (!email) {
      return { error: new Error('No signed-in email to verify.') };
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const updatePassword = async (password) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (!error) setPasswordRecovery(false);
    return { data, error };
  };

  /** Sends a confirmation link; email does not change until the user confirms. */
  const updateEmail = async (email) => {
    const { data, error } = await supabase.auth.updateUser(
      { email },
      { emailRedirectTo: window.location.origin }
    );
    return { data, error };
  };

  /** Emails a password-reset link; password is only set after the user opens it. */
  const requestPasswordReset = async (email) => {
    const target = email || session?.user?.email;
    if (!target) {
      return { error: new Error('No email address for password reset.') };
    }
    const { data, error } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: `${window.location.origin}/`,
    });
    return { data, error };
  };

  const clearPasswordRecovery = () => setPasswordRecovery(false);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        passwordRecovery,
        clearPasswordRecovery,
        signUp,
        signIn,
        signOut,
        reauthenticate,
        updatePassword,
        updateEmail,
        requestPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
